// Transfer search as URL query values, shared by the search widget, results and checkout pages.
// Query: journey=airport|outstation|local, pickup / drop (place names) with optional pickupId / dropId
// (place ids from the suggestions), date + time (local pickup time), returnDate + returnTime for a
// round trip, passengers.
import { addDaysIso, fmtDay, todayIso } from '../../shared/data/common';

export const journeys = [
  ['airport', 'Airport transfer', 'To or from the airport, with meet & greet'],
  ['outstation', 'Outstation', 'City to city, one way or round trip'],
  ['local', 'Local', 'Anywhere within your city'],
];

const journeyType = { airport: 'AirportTransfer', outstation: 'Outstation', local: 'Local' };
export const journeyLabel = (journey) => (journeys.find(([k]) => k === journey) || journeys[0])[1];
export const journeyFromType = (type) => Object.keys(journeyType).find((k) => journeyType[k] === type) || 'airport';

export const MAX_PASSENGERS = 10;
export const MIN_NOTICE_HOURS = 2;

const time = (value, fallback) => (/^\d{2}:\d{2}$/.test(value || '') ? value : fallback);

/** Search values from URL query parameters, with defaults for anything missing. */
export function parseTransferQuery(q) {
  const date = q.date && q.date >= todayIso() ? q.date : addDaysIso(7);
  const passengers = Math.min(MAX_PASSENGERS, Math.max(1, Number(q.passengers) || 1));
  const returnDate = q.returnDate && q.returnDate >= date ? q.returnDate : '';
  return {
    journey: journeyType[q.journey] ? q.journey : 'airport',
    pickup: { name: (q.pickup || '').trim(), placeId: q.pickupId || '' },
    drop: { name: (q.drop || '').trim(), placeId: q.dropId || '' },
    date,
    time: time(q.time, '10:00'),
    returnDate,
    returnTime: returnDate ? time(q.returnTime, '18:00') : '',
    passengers,
  };
}

export const isRoundTrip = (s) => Boolean(s.returnDate);

/** URL query values for a search; return fields only for a round trip. */
export function toTransferParams(s) {
  return {
    journey: s.journey,
    pickup: s.pickup.name,
    ...(s.pickup.placeId ? { pickupId: s.pickup.placeId } : {}),
    drop: s.drop.name,
    ...(s.drop.placeId ? { dropId: s.drop.placeId } : {}),
    date: s.date,
    time: s.time,
    ...(isRoundTrip(s) ? { returnDate: s.returnDate, returnTime: s.returnTime } : {}),
    passengers: String(s.passengers),
  };
}

export const transferQueryString = (s) => new URLSearchParams(toTransferParams(s)).toString();

/** API search request once the pickup and drop points are resolved to coordinates. */
export function toSearchRequest(s, origin, destination) {
  return {
    journeyType: journeyType[s.journey],
    tripType: isRoundTrip(s) ? 'RoundTrip' : 'OneWay',
    origin,
    destination,
    pickupDateTime: `${s.date}T${s.time}`,
    ...(isRoundTrip(s) ? { returnDateTime: `${s.returnDate}T${s.returnTime}` } : {}),
    passengers: s.passengers,
  };
}

/** Problems the API would reject, caught before searching. Times are compared in the browser's time zone. */
export function searchProblem(s, now = new Date()) {
  if (!s.pickup.name) return 'Enter a pickup location.';
  if (!s.drop.name) return 'Enter a drop location.';
  if (s.pickup.placeId && s.pickup.placeId === s.drop.placeId) return 'Pickup and drop must be different places.';
  const pickupAt = new Date(`${s.date}T${s.time}`);
  if (pickupAt - now < MIN_NOTICE_HOURS * 36e5) return `Pickup must be at least ${MIN_NOTICE_HOURS} hours from now.`;
  if (isRoundTrip(s) && new Date(`${s.returnDate}T${s.returnTime}`) - pickupAt < 30 * 6e4) {
    return 'The return pickup must be at least 30 minutes after the first pickup.';
  }
  return null;
}

/** "2026-10-27T10:00:00" → { date: "2026-10-27", time: "10:00" }. */
export const splitDateTime = (value) => ({ date: String(value || '').slice(0, 10), time: String(value || '').slice(11, 16) });

/** "10:00" → "10:00 AM". */
export function clock(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm || '';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

export const whenLabel = (date, hhmm) => `${fmtDay(date)}, ${clock(hhmm)}`;

/** First part of an address: "Connaught Place, New Delhi, Delhi, India" → "Connaught Place". */
export const shortPlace = (name) => String(name || '').split(',')[0].trim();

export const passengersLabel = (n) => `${n} passenger${n === 1 ? '' : 's'}`;

/** 229 → "3h 49m". */
export function durationLabel(minutes) {
  if (!minutes) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`;
}
